import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import PublicLayout from '@/components/layout/PublicLayout';
import { supabase } from '@/integrations/supabase/client';
import { organizationService } from '@/services/organizationService';
import { welcomeWeekService } from '@/services/welcomeWeekService';
import { useNavigate } from 'react-router-dom';
import type { Organization } from '@/types/organization';
import type { WelcomeWeekPublicOverview } from '@/types/welcomeWeek';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Building2, Calendar, Users, MapPin, Clock, ExternalLink, ArrowLeft } from 'lucide-react';
import { richHtml } from '@/utils/safeHtml';

type OrgPublic = Pick<Organization, 'id' | 'name' | 'slug' | 'description' | 'avatar_url'>;

export default function OrgPublicPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [org, setOrg] = useState<OrgPublic | null>(null);
  const [events, setEvents] = useState<any[]>([]);
  const [wwOverview, setWwOverview] = useState<WelcomeWeekPublicOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState<string | null>(null);
  // ids of the WW groups the signed-in user belongs to (is a group admin of)
  const [myGroupIds, setMyGroupIds] = useState<string[]>([]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const normalized = (slug || '').toLowerCase();
        // Case-insensitive slug lookup using the database function
        const { data: orgRows } = await (supabase as any)
          .rpc('get_public_org_by_slug', { org_slug: normalized });

        const orgRow = orgRows?.[0];
        if (!orgRow) { setOrg(null); setEvents([]); return; }
        setOrg(orgRow as OrgPublic);
        try {
          setWwOverview(await welcomeWeekService.getPublicOverview((orgRow as any).slug));
        } catch (welcomeWeekError) {
          console.warn('[OrgPublic] Welcome-week overview unavailable:', welcomeWeekError);
          setWwOverview(null);
        }
        const { data: ev } = await supabase
          .from('events')
          .select('*')
          .eq('organization_id', (orgRow as any).id)
          .eq('is_private', false)
          .gte('start_time', new Date().toISOString())
          .order('start_time', { ascending: true });
        setEvents(ev || []);

        // Check if user is a member of this organization
        try {
          const memberships = await organizationService.myMemberships();
          const membership = memberships.find(m => m.organization_id === (orgRow as any).id);
          setUserRole(membership?.role || null);
        } catch (error) {
          // User not authenticated or not a member
          setUserRole(null);
        }

        // Which WW group(s) does this user belong to? Used to scope the summary card.
        try {
          const myGroups = await welcomeWeekService.listMyAdminGroups((orgRow as any).id);
          setMyGroupIds(myGroups.map((g) => g.id));
        } catch {
          setMyGroupIds([]);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  // Scope the Welcome Week summary to the user's own group when they belong to one
  // and are not an org owner/admin (managers keep the full overview).
  const isManager = userRole === 'owner' || userRole === 'admin';
  const wwGroups = wwOverview?.groups || [];
  const myWwGroups = wwGroups.filter((g) => myGroupIds.includes(g.id));
  const filterToMyGroups = !isManager && myWwGroups.length > 0;
  const shownWwGroupCount = filterToMyGroups ? myWwGroups.length : wwGroups.length;
  const singleWwGroup = filterToMyGroups && myWwGroups.length === 1 ? myWwGroups[0] : null;

  if (loading) {
    return (
      <PublicLayout>
        <div className="container py-8">
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <Building2 className="h-12 w-12 mx-auto text-muted-foreground animate-pulse" />
              <p className="mt-4 text-muted-foreground">Loading organization...</p>
            </div>
          </div>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <div className="container py-8 space-y-8">
        {!org ? (
          <Card>
            <CardContent className="text-center py-12">
              <Building2 className="h-16 w-16 mx-auto text-muted-foreground" />
              <h1 className="text-2xl font-semibold mb-2 mt-4">Organization not found</h1>
              <p className="text-muted-foreground">This organization is not public or does not exist.</p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Back button for members/owners */}
            {userRole && (
              <div className="mb-4">
                <Button
                  variant="ghost"
                  onClick={() => navigate('/organizations')}
                  className="flex items-center gap-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to Organizations
                </Button>
              </div>
            )}

            {/* Organization Header */}
            <Card className="overflow-hidden">
              <div className="bg-gradient-to-r from-primary/10 to-primary/5 p-8">
                <div className="flex items-start gap-6">
                  <Avatar className="h-24 w-24 ring-4 ring-background shadow-lg">
                    <AvatarImage src={org.avatar_url || ''} alt={org.name} />
                    <AvatarFallback className="bg-gradient-primary text-white text-2xl">
                      <Building2 className="h-12 w-12" />
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <h1
                      className="text-4xl font-bold text-foreground mb-2 cursor-pointer hover:text-primary transition-colors"
                      onClick={() => navigate('/organizations')}
                    >
                      {org.name}
                    </h1>
                    <p
                      className="text-lg text-muted-foreground font-medium mb-4 cursor-pointer hover:text-primary transition-colors"
                      onClick={() => navigate('/organizations')}
                    >
                      @{org.slug}
                    </p>
                    {org.description && (
                      <p className="text-muted-foreground leading-relaxed max-w-3xl">
                        {org.description}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </Card>

            {wwOverview?.season && (
              <Card>
                <CardContent className="p-6">
                  <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <div>
                      <h2 className="text-xl font-semibold">Welcome Week</h2>
                      <p className="text-sm text-muted-foreground">
                        {wwOverview.season.name} -{' '}
                        {singleWwGroup ? singleWwGroup.name : `${shownWwGroupCount} groups`}
                      </p>
                    </div>
                    <Button asChild>
                      <Link
                        to={singleWwGroup
                          ? `/o/${org.slug}/ww/${singleWwGroup.slug}`
                          : `/o/${org.slug}/ww`}
                        className="flex items-center gap-2"
                      >
                        <Calendar className="h-4 w-4" />
                        Open Schedule
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Events Section */}
            <div>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-foreground mb-2">Upcoming Events</h2>
                  <p className="text-muted-foreground">Public events hosted by {org.name}</p>
                </div>
                <Badge variant="secondary" className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  {events.length} events
                </Badge>
              </div>

              {events.length === 0 ? (
                <Card>
                  <CardContent className="text-center py-12">
                    <Calendar className="h-16 w-16 mx-auto text-muted-foreground" />
                    <h3 className="text-lg font-semibold mt-4">No upcoming events</h3>
                    <p className="text-muted-foreground mt-2 mb-4">
                      {org.name} hasn't scheduled any public events yet.
                    </p>
                    {userRole && (userRole === 'owner' || userRole === 'admin') && (
                      <Button asChild>
                        <Link to={`/dashboard/new?org=${org.id}`} className="flex items-center gap-2">
                          <Calendar className="h-4 w-4" />
                          Create Event
                        </Link>
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-4">
                  {events.map(event => (
                    <Card key={event.id} className="hover-scale transition-all duration-200 hover:shadow-md">
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <h3 className="text-xl font-semibold text-foreground mb-2 line-clamp-2">
                              {event.title}
                            </h3>

                            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-4">
                              <div className="flex items-center gap-2">
                                <Clock className="h-4 w-4" />
                                <span>{new Date(event.start_time).toLocaleDateString('en-US', {
                                  weekday: 'long',
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}</span>
                              </div>

                              {event.location && (
                                <div className="flex items-center gap-2">
                                  <MapPin className="h-4 w-4" />
                                 <span className="truncate">
                                   {typeof event.location === 'string'
                                     ? event.location
                                     : event.location?.name || event.location?.address || 'Location TBD'
                                   }
                                 </span>
                                </div>
                              )}

                              {event.capacity && (
                                <div className="flex items-center gap-2">
                                  <Users className="h-4 w-4" />
                                  <span>{event.capacity} spots</span>
                                </div>
                              )}
                            </div>

                            {event.description && (
                              <div
                                className="text-muted-foreground text-sm line-clamp-2 mb-4"
                                dangerouslySetInnerHTML={richHtml(event.description)}
                              />
                            )}
                          </div>

                          <div className="ml-6 flex flex-col items-end gap-2">
                            <Button asChild>
                              <Link to={`/${event.slug}`} className="flex items-center gap-2">
                                View Event
                                <ExternalLink className="h-4 w-4" />
                              </Link>
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </PublicLayout>
  );
}
